// Anonymous, privacy-first analytics SDK. No PII; respects opt-out (en_analytics=declined) and Global Privacy Control.
const API = `${process.env.REACT_APP_BACKEND_URL}/api/collect`;
const VISITOR_KEY = "en_vid", SESSION_KEY = "en_sid", SESSION_TTL = 30 * 60 * 1000;
const KEEP_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const CLICK_IDS = ["gclid", "fbclid", "msclkid"];

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`);
export const getCookie = (n) => document.cookie.split("; ").find((c) => c.startsWith(n + "="))?.split("=")[1];
export const setCookie = (n, v, days) => { document.cookie = `${n}=${v}; Max-Age=${days * 86400}; Path=/; SameSite=Lax; Secure`; };

export const analyticsEnabled = () => getCookie("en_analytics") !== "declined" && navigator.globalPrivacyControl !== true;
export const setAnalyticsConsent = (on) => setCookie("en_analytics", on ? "accepted" : "declined", 400);

const device = () => {
  const ua = navigator.userAgent;
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return "tablet";
  return /Mobi|Android|iPhone|iPod/i.test(ua) ? "mobile" : "desktop";
};

const readParams = () => {
  const p = new URLSearchParams(window.location.search);
  const utm = {}, click_ids = {};
  KEEP_PARAMS.forEach((k) => { if (p.get(k)) utm[k] = p.get(k).slice(0, 100); });
  CLICK_IDS.forEach((k) => { if (p.has(k)) click_ids[k] = true; });
  return { utm, click_ids };
};

const state = { visitor_id: null, session_id: null, page_view_id: null, sequence: 0, new_session: false, landing: null, queue: [], timer: null };

const visitorId = () => {
  let id = getCookie(VISITOR_KEY) || localStorage.getItem(VISITOR_KEY);
  if (!id) id = uuid();
  setCookie(VISITOR_KEY, id, 395);
  localStorage.setItem(VISITOR_KEY, id);
  return id;
};

const sessionId = (hasCampaign) => {
  let s = null;
  try { s = JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { s = null; }
  const stale = !s || Date.now() - s.last > SESSION_TTL;
  const campaignKey = hasCampaign ? JSON.stringify(readParams().utm) + document.referrer : null;
  const newCampaign = hasCampaign && s && s.campaign !== campaignKey;
  if (stale || newCampaign) { s = { id: uuid(), last: Date.now(), campaign: campaignKey, seq: 0 }; state.new_session = true; }
  s.last = Date.now();
  localStorage.setItem(SESSION_KEY, JSON.stringify(s));
  return s;
};

const touch = () => {
  try { const s = JSON.parse(localStorage.getItem(SESSION_KEY)); if (s) { s.last = Date.now(); s.seq = state.sequence; localStorage.setItem(SESSION_KEY, JSON.stringify(s)); } } catch { /* ignore */ }
};

export const init = () => {
  if (!analyticsEnabled()) return false;
  state.visitor_id = visitorId();
  const { utm, click_ids } = readParams();
  const hasCampaign = Object.keys(utm).length > 0 || Object.keys(click_ids).length > 0;
  const s = sessionId(hasCampaign);
  state.session_id = s.id;
  state.sequence = s.seq || 0;
  if (state.new_session || !s.landing) { s.landing = { utm, click_ids, referrer: document.referrer || null }; localStorage.setItem(SESSION_KEY, JSON.stringify(s)); }
  state.landing = s.landing;
  return true;
};

export const ids = () => ({ visitor_id: state.visitor_id, session_id: state.session_id, page_view_id: state.page_view_id });

const base = () => ({
  ts: Date.now(), visitor_id: state.visitor_id, session_id: state.session_id, page_view_id: state.page_view_id,
  path: window.location.pathname, device: device(), viewport: `${window.innerWidth}x${window.innerHeight}`, tz_offset: new Date().getTimezoneOffset(), sequence: state.sequence,
});

export const flush = (beacon = false) => {
  if (!state.queue.length) return;
  const body = JSON.stringify({ events: state.queue.splice(0, 100) });
  clearTimeout(state.timer); state.timer = null;
  if (beacon && navigator.sendBeacon) { navigator.sendBeacon(API, new Blob([body], { type: "application/json" })); return; }
  const send = (retry) => fetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body, credentials: "include" })
    .then((r) => { if (r.status === 204) { state.disabled = true; state.queue = []; } else if (!r.ok && retry) return send(false); })
    .catch(() => retry && send(false));
  send(true);
};

export const isAdminPath = () => window.location.pathname.startsWith("/admin");

export const track = (type, payload = {}, extra = {}) => {
  if (!state.visitor_id || state.disabled || !analyticsEnabled() || isAdminPath()) return;
  state.queue.push({ type, payload, ...base(), ...extra });
  touch();
  if (type === "page_view" || type === "form_submit_success" || state.queue.length >= 10) flush();
  else if (!state.timer) state.timer = setTimeout(() => flush(), 2000);
};

export const pageView = () => {
  const path = window.location.pathname;
  if (isAdminPath()) return;
  if (state.lastPv && state.lastPv.path === path && Date.now() - state.lastPv.at < 2000) return;
  state.lastPv = { path, at: Date.now() };
  state.page_view_id = uuid();
  state.sequence += 1;
  const first = state.sequence === 1 || state.new_session;
  state.new_session = false;
  track("page_view", {}, { title: document.title, ...state.landing, new_session: first });
};
